import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';

const getEngineerBadgeStyle = (score) => {
  if (score >= 900) return 'bg-yellow-100 text-yellow-700 border border-yellow-300';
  if (score >= 800) return 'bg-purple-100 text-purple-700 border border-purple-300';
  if (score >= 700) return 'bg-indigo-100 text-indigo-700 border border-indigo-300';
  if (score >= 550) return 'bg-blue-100 text-blue-700 border border-blue-300';
  if (score >= 400) return 'bg-green-100 text-green-700 border border-green-300';
  if (score >= 200) return 'bg-orange-100 text-orange-700 border border-orange-300';
  return 'bg-gray-100 text-gray-700 border border-gray-300';
};

const getEngineerBadgeIcon = (score) => {
  if (score >= 900) return 'bx-crown';
  if (score >= 800) return 'bx-medal';
  if (score >= 700) return 'bx-trophy';
  if (score >= 550) return 'bx-star';
  if (score >= 400) return 'bx-trending-up';
  if (score >= 200) return 'bx-book-open';
  return 'bx-rocket';
};

const getEngineerLevelMeta = (score) => {
  if (score >= 900) return { badge: 'bg-yellow-100 text-yellow-700 border border-yellow-300', icon: 'bx-crown' };
  if (score >= 800) return { badge: 'bg-purple-100 text-purple-700 border border-purple-300', icon: 'bx-medal' };
  if (score >= 700) return { badge: 'bg-indigo-100 text-indigo-700 border border-indigo-300', icon: 'bx-trophy' };
  if (score >= 550) return { badge: 'bg-blue-100 text-blue-700 border border-blue-300', icon: 'bx-star' };
  if (score >= 400) return { badge: 'bg-green-100 text-green-700 border border-green-300', icon: 'bx-trending-up' };
  if (score >= 200) return { badge: 'bg-orange-100 text-orange-700 border border-orange-300', icon: 'bx-book-open' };
  return { badge: 'bg-gray-100 text-gray-700 border border-gray-300', icon: 'bx-rocket' };
};

const skillMeta = {
  global_engineer_score: {
    label: 'Engineering Score',
    icon: 'bx-code-block',
    accent: 'from-sky-500 to-cyan-500',
    bg: 'bg-sky-50',
    text: 'text-sky-700',
    border: 'border-sky-100'
  },
  aptitude: {
    label: 'Aptitude',
    icon: 'bx-brain',
    accent: 'from-emerald-500 to-teal-500',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-100'
  },
  verbal: {
    label: 'Verbal',
    icon: 'bx-message-rounded-detail',
    accent: 'from-fuchsia-500 to-rose-500',
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    border: 'border-rose-100'
  },
  problem_solving: {
    label: 'Problem Solving',
    icon: 'bx-bulb',
    accent: 'from-amber-500 to-orange-500',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-100'
  }
};

const globalLevelConfig = [
  { min: 0, max: 199, label: 'Beginner' },
  { min: 200, max: 399, label: 'Learner' },
  { min: 400, max: 549, label: 'Intermediate' },
  { min: 550, max: 699, label: 'Strong' },
  { min: 700, max: 799, label: 'Advanced' },
  { min: 800, max: 899, label: 'Expert' },
  { min: 900, max: 1000, label: 'Elite' }
];

const skillLevelConfig = [
  { min: 0, max: 20, label: 'Level 1', icon: 'bx-shield-quarter', badge: 'bg-slate-100 text-slate-700 border border-slate-300' },
  { min: 21, max: 40, label: 'Level 2', icon: 'bx-medal', badge: 'bg-amber-100 text-amber-700 border border-amber-300' },
  { min: 41, max: 60, label: 'Level 3', icon: 'bx-badge-check', badge: 'bg-rose-100 text-rose-700 border border-rose-300' },
  { min: 61, max: 80, label: 'Level 4', icon: 'bx-diamond', badge: 'bg-cyan-100 text-cyan-700 border border-cyan-300' },
  { min: 81, max: 100, label: 'Level 5', icon: 'bx-crown', badge: 'bg-yellow-100 text-yellow-700 border border-yellow-300' }
];

const getGlobalLevel = (score) => {
  return globalLevelConfig.find((item) => score >= item.min && score <= item.max)?.label || 'Beginner';
};

const getSkillLevelMeta = (score) => {
  return skillLevelConfig.find((item) => score >= item.min && score <= item.max) || skillLevelConfig[0];
};

const buildChartPoints = (points, height = 72, width = 220) => {
  if (!points.length) {
    return [];
  }

  const max = Math.max(...points.map((point) => point.value), 1);
  const min = Math.min(...points.map((point) => point.value), 0);
  const range = Math.max(max - min, 1);

  return points.map((point, index) => {
    const x = points.length === 1 ? 0 : (index / (points.length - 1)) * width;
    const y = height - (((point.value - min) / range) * (height - 10)) - 5;
    return {
      ...point,
      x,
      y
    };
  });
};

const buildPath = (points, height = 72, width = 220) => {
  return buildChartPoints(points, height, width)
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
    .join(' ');
};

const getDelta = (points, currentValue) => {
  if (!points?.length) {
    return null;
  }

  const lastSnapshotValue = points[points.length - 1].value;
  return currentValue - lastSnapshotValue;
};

const formatPeriodLabel = (periodKey) => {
  if (!periodKey) {
    return '';
  }

  return periodKey.replace('-W', ' / W');
};

const formatScoreDisplay = (key, score) => {
  return key === 'global_engineer_score' ? `${score} / 1000` : `${score} / 100`;
};

const Home = () => {
  const { user } = useAuth();
  const [rankings, setRankings] = useState(null);
  const [history, setHistory] = useState({ snapshots: {}, assessments: [] });
  const [loading, setLoading] = useState(true);
  const [hoveredPoint, setHoveredPoint] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [rankingsResponse, historyResponse] = await Promise.all([
          api.get('/student/rankings'),
          api.get('/student/score-history')
        ]);

        setRankings(rankingsResponse.data.rankings);
        setHistory(historyResponse.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const scoreCards = useMemo(() => {
    const currentScores = {
      global_engineer_score: user?.global_engineer_score || 0,
      aptitude: user?.skill_scores?.aptitude || 0,
      verbal: user?.skill_scores?.verbal || 0,
      problem_solving: user?.skill_scores?.problem_solving || 0
    };

    return Object.keys(skillMeta).map((key) => {
      const score = currentScores[key];
      const points = history.snapshots?.[key] || [];
      return {
        key,
        ...skillMeta[key],
        score,
        level: key === 'global_engineer_score'
          ? getGlobalLevel(score)
          : `Level ${user?.skill_levels?.[key] || 1}`,
        points,
        delta: getDelta(points, score)
      };
    });
  }, [history.snapshots, user]);

  const recentAssessments = useMemo(() => {
    return [...(history.assessments || [])]
      .sort((a, b) => new Date(b.assessment_date || b.attempted_at) - new Date(a.assessment_date || a.attempted_at))
      .slice(0, 5);
  }, [history.assessments]);

  if (loading) {
    return <div className="py-12 text-center">Loading...</div>;
  }

  return (
    <div className="mx-auto max-w-7xl space-y-8">
      <section className="overflow-hidden rounded-[2rem] border border-blue-200 bg-gradient-to-r from-[#dbe8ff] via-[#edf4ff] to-[#f8fbff] text-slate-900 shadow-lg">
        <div className="grid gap-6 p-6 lg:grid-cols-[1.7fr_0.9fr]">
          <div className="flex flex-col justify-center">
            <p className="text-xs uppercase tracking-[0.35em] text-blue-700">Verified Student Home</p>
            <div className="mt-3 flex flex-col gap-4 md:flex-row md:items-center">
              <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-3xl border border-blue-200 bg-white shadow-sm">
                {user?.profile_photo ? (
                  <img
                    src={user.profile_photo.startsWith('http') ? user.profile_photo : `${process.env.REACT_APP_IMG_URL || 'http://localhost:5000'}${user.profile_photo}`}
                    alt="Profile"
                    className="h-20 w-20 object-cover"
                  />
                ) : (
                  <i className="bx bx-user text-4xl text-blue-500"></i>
                )}
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-2xl font-bold md:text-3xl">{user?.name}</h1>
                  <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${getEngineerBadgeStyle(user?.global_engineer_score || 0)}`}>
                    <i className={`bx ${getEngineerBadgeIcon(user?.global_engineer_score || 0)}`}></i>
                    {getGlobalLevel(user?.global_engineer_score || 0)}
                  </span>
                </div>
                <p className="mt-1 text-slate-600">{user?.college?.name}</p>
                <div className="mt-3 flex flex-wrap gap-2 text-sm text-slate-700">
                  <span className="rounded-full border border-blue-200 bg-white px-3 py-1">Course: {user?.course}</span>
                  <span className="rounded-full border border-blue-200 bg-white px-3 py-1">Graduation: {user?.graduation_year}</span>
                  <span className="rounded-full border border-blue-200 bg-white px-3 py-1">Academic Year: {user?.academic_year || '-'}</span>
                </div>
              </div>
            </div>
            <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-600">
              Your profile now combines verified coding-platform data with college assessment evidence, so the summary here stays useful even as new tests and shareable profile features are added later.
            </p>
          </div>

          <div className="rounded-[1.5rem] border border-blue-200 bg-white/95 p-5 shadow-sm">
            <p className="text-xs uppercase tracking-[0.25em] text-blue-700">Current coding tier</p>
            <div className="mt-3 flex items-center gap-3">
              <span className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${getEngineerBadgeStyle(user?.global_engineer_score || 0)}`}>
                <i className={`bx ${getEngineerBadgeIcon(user?.global_engineer_score || 0)} text-2xl`}></i>
              </span>
              <div>
                <p className="text-3xl font-bold text-slate-900">{user?.global_engineer_score || 0} / 1000</p>
                <p className="text-sm font-semibold text-slate-600">{getGlobalLevel(user?.global_engineer_score || 0)}</p>
              </div>
            </div>
            <div className="mt-4 space-y-3 text-sm text-slate-700">
              <div className="flex items-center justify-between rounded-2xl bg-blue-50 px-4 py-3">
                <span>Global rank</span>
                <strong>#{rankings?.global?.rank || '-'}</strong>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-blue-50 px-4 py-3">
                <span>College rank</span>
                <strong>#{rankings?.college?.rank || '-'}</strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-5 xl:grid-cols-4 md:grid-cols-2">
        {scoreCards.map((card) => {
          const skillLevelMeta = getSkillLevelMeta(card.score);

          return (
            <div key={card.key} className={`rounded-[1.75rem] border ${card.border} bg-white p-5 shadow-sm`}>
              <div className="flex items-center justify-between">
                <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${card.bg} ${card.text}`}>
                  <i className={`bx ${card.icon} text-2xl`}></i>
                </div>
                {card.key === 'global_engineer_score' ? (
                  <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${getEngineerBadgeStyle(card.score)}`}>
                    <i className={`bx ${getEngineerBadgeIcon(card.score)}`}></i>
                    {card.level}
                  </span>
                ) : (
                  <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${skillLevelMeta.badge}`}>
                    <i className={`bx ${skillLevelMeta.icon}`}></i>
                    {card.level}
                  </span>
                )}
              </div>
              <p className="mt-4 text-sm font-semibold text-slate-500">{card.label}</p>
              <div className="mt-2 flex items-end justify-between gap-3">
                <p className="text-3xl font-bold text-slate-900">{formatScoreDisplay(card.key, card.score)}</p>
                <span className={`text-sm font-semibold ${card.delta > 0 ? 'text-emerald-600' : card.delta < 0 ? 'text-rose-600' : 'text-slate-400'}`}>
                  {card.delta === null ? 'No baseline' : `${card.delta > 0 ? '+' : ''}${card.delta}`}
                </span>
              </div>
            </div>
          );
        })}
      </section>

      <section className="space-y-5">
        {scoreCards.map((card) => (
          <div key={`${card.key}-graph`} className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${card.bg} ${card.text}`}>
                    <i className={`bx ${card.icon} text-2xl`}></i>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">{card.label} Progress</h2>
                    <p className="text-sm text-slate-500">
                      {card.key === 'global_engineer_score'
                        ? 'Weekly coding-score trend from verified platform sync'
                        : 'Weekly trend from college assessment aggregation'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 text-sm">
                <div className="rounded-2xl bg-slate-50 px-4 py-3">
                  <p className="text-slate-500">Current</p>
                  <p className="font-bold text-slate-900">{formatScoreDisplay(card.key, card.score)}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 px-4 py-3">
                  <p className="text-slate-500">Level</p>
                  <p className="font-bold text-slate-900">{card.level}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 px-4 py-3">
                  <p className="text-slate-500">Change</p>
                  <p className={`${card.delta > 0 ? 'text-emerald-600' : card.delta < 0 ? 'text-rose-600' : 'text-slate-900'} font-bold`}>
                    {card.delta === null ? 'No baseline' : `${card.delta > 0 ? '+' : ''}${card.delta}`}
                  </p>
                </div>
              </div>
            </div>

            <div className={`mt-6 rounded-[1.5rem] bg-gradient-to-r ${card.accent} p-[1px]`}>
              <div className="rounded-[1.5rem] bg-white p-5">
                {card.points.length > 0 ? (
                  <div className="relative">
                    {(() => {
                      const chartPoints = buildChartPoints(card.points, 180, 860);

                      return (
                        <>
                          {hoveredPoint?.cardKey === card.key && (
                            <div
                              className="pointer-events-none absolute z-10 -translate-x-1/2 rounded-2xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white shadow-lg"
                              style={{
                                left: `${((hoveredPoint.x + 20) / 900) * 100}%`,
                                top: `${Math.max(0, hoveredPoint.y - 18)}px`
                              }}
                            >
                              {formatPeriodLabel(hoveredPoint.period_key)}: {hoveredPoint.value}
                            </div>
                          )}
                    
                          <svg viewBox="0 0 900 220" className="h-52 w-full">
                            <path
                              d={buildPath(card.points, 180, 860)}
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="4"
                              className={card.text}
                              transform="translate(20 20)"
                            ></path>
                            {chartPoints.map((point) => (
                              <circle
                                key={`${card.key}-${point.period_key}`}
                                cx={point.x + 20}
                                cy={point.y + 20}
                                r="6"
                                className={`${card.text} cursor-pointer fill-current stroke-white`}
                                strokeWidth="3"
                                onMouseEnter={() => setHoveredPoint({ ...point, cardKey: card.key })}
                                onMouseLeave={() => setHoveredPoint((current) => (
                                  current?.cardKey === card.key && current?.period_key === point.period_key
                                    ? null
                                    : current
                                ))}
                              >
                                <title>{`${formatPeriodLabel(point.period_key)}: ${point.value}`}</title>
                              </circle>
                            ))}
                          </svg>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="flex h-52 items-center justify-center rounded-[1.25rem] bg-slate-50 text-sm text-slate-400">
                    Weekly history will appear here once snapshots are available.
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Rank snapshot</h2>
              <p className="text-sm text-slate-500">Four views of your coding rank based on global engineer score.</p>
            </div>
          </div>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div className="rounded-3xl bg-sky-50 p-5">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-100 text-sky-700">
                  <i className="bx bx-buildings text-2xl"></i>
                </div>
                <p className="text-sm font-semibold text-sky-700">College + your year</p>
              </div>
              <p className="mt-3 text-4xl font-bold text-slate-900">#{rankings?.college_year?.rank || '-'}</p>
              <p className="mt-2 text-sm text-slate-500">Out of {rankings?.college_year?.total || 0}</p>
            </div>
            <div className="rounded-3xl bg-emerald-50 p-5">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                  <i className="bx bx-globe text-2xl"></i>
                </div>
                <p className="text-sm font-semibold text-emerald-700">Global + your year</p>
              </div>
              <p className="mt-3 text-4xl font-bold text-slate-900">#{rankings?.global_year?.rank || '-'}</p>
              <p className="mt-2 text-sm text-slate-500">Out of {rankings?.global_year?.total || 0}</p>
            </div>
            <div className="rounded-3xl bg-fuchsia-50 p-5">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-fuchsia-100 text-fuchsia-700">
                  <i className="bx bx-medal text-2xl"></i>
                </div>
                <p className="text-sm font-semibold text-fuchsia-700">College overall</p>
              </div>
              <p className="mt-3 text-4xl font-bold text-slate-900">#{rankings?.college?.rank || '-'}</p>
              <p className="mt-2 text-sm text-slate-500">Out of {rankings?.college?.total || 0}</p>
            </div>
            <div className="rounded-3xl bg-amber-50 p-5">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
                  <i className="bx bx-trophy text-2xl"></i>
                </div>
                <p className="text-sm font-semibold text-amber-700">Global overall</p>
              </div>
              <p className="mt-3 text-4xl font-bold text-slate-900">#{rankings?.global?.rank || '-'}</p>
              <p className="mt-2 text-sm text-slate-500">Out of {rankings?.global?.total || 0}</p>
            </div>
          </div>
        </div>

        <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-2xl font-bold text-slate-900">Recent verified evidence</h2>
          <p className="text-sm text-slate-500">The latest college assessments feeding your home summary.</p>
          <div className="mt-5 space-y-3">
            {recentAssessments.length === 0 ? (
              <div className="rounded-3xl bg-slate-50 p-5 text-sm text-slate-500">
                No assessment results yet. Once your college uploads marks, they will appear here and update your skill scores.
              </div>
            ) : (
              recentAssessments.map((item) => (
                <div key={item.id} className="rounded-3xl border border-slate-200 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-semibold text-slate-900">{item.assessment_name}</p>
                      <p className="mt-1 text-sm text-slate-500">
                        {`${skillMeta[item.category]?.label || item.category} • ${item.marks_obtained}/${item.max_marks}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-slate-900">{item.normalized_score} / 100</p>
                      <p className="text-xs text-slate-500">Level {item.score_level}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-5">
            <h2 className="text-2xl font-bold text-slate-900">Global Engineer Score Levels</h2>
            <p className="text-sm text-slate-500">Your coding rank title is derived from these score bands.</p>
          </div>
          <div className="space-y-3">
            {globalLevelConfig.map((item) => {
              const active = (user?.global_engineer_score || 0) >= item.min && (user?.global_engineer_score || 0) <= item.max;
              const bandScore = item.min === 0 ? 0 : item.min;
              const bandMeta = getEngineerLevelMeta(bandScore);

              return (
                <div
                  key={item.label}
                  className={`flex items-center justify-between rounded-2xl px-4 py-4 ${
                    active ? `border-2 ${bandMeta.badge}` : bandMeta.badge
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl ${bandMeta.badge}`}>
                      <i className={`bx ${bandMeta.icon} text-xl`}></i>
                    </span>
                    <div>
                      <p className="font-semibold text-slate-900">{item.label}</p>
                      <p className="text-sm text-slate-500">{item.min} - {item.max}</p>
                    </div>
                  </div>
                  {active && (
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#2d7bd0] text-white shadow-md">
                      <i className="bx bx-check text-2xl"></i>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-5">
            <h2 className="text-2xl font-bold text-slate-900">Skill Level Bands</h2>
            <p className="text-sm text-slate-500">Aptitude, verbal, and problem solving scores use equal 20-point level ranges.</p>
          </div>
          <div className="space-y-3">
            {skillLevelConfig.map((item) => {
              const categoriesInBand = ['aptitude', 'verbal', 'problem_solving'].filter((key) => {
                const score = user?.skill_scores?.[key] || 0;
                return score >= item.min && score <= item.max;
              });

              return (
                <div key={item.label} className={`rounded-2xl border px-4 py-4 ${item.badge}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl ${item.badge}`}>
                        <i className={`bx ${item.icon} text-xl`}></i>
                      </span>
                      <div>
                        <p className="font-semibold text-slate-900">{item.label}</p>
                        <p className="text-sm text-slate-500">{item.min} - {item.max}</p>
                      </div>
                    </div>
                    {categoriesInBand.length > 0 && (
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#2d7bd0] text-white shadow-md">
                        <i className="bx bx-check text-2xl"></i>
                      </span>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {['aptitude', 'verbal', 'problem_solving'].map((key) => {
                      const score = user?.skill_scores?.[key] || 0;
                      const active = categoriesInBand.includes(key);
                      return (
                        <span
                          key={`${item.label}-${key}`}
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            active ? 'bg-slate-900 text-white' : 'bg-white text-slate-500'
                          }`}
                        >
                          {skillMeta[key].label}: {score} / 100
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
